import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { ClassTypeLabel } from "./ClassTypeLabel";
import type { ClassInterface } from "../../types/class.interface";
import { Link } from "react-router-dom";
import { addToFavorites, removeFromFavorites, enrollInClass, type EnrollClassRequest, inviteFriendToClass } from "../../services/schedule.service";
import { cancelUserClass } from "../../services/class.service";
import { EnrollInClassModal, ReservationConfirmedModal, ErrorModal, CancelClassModal, SuccessModal, InviteAFriendModal, useCurrentUser } from '@beholos/holoscommon';
import { Info, Lock, Heart, Star, XCircle, MinusCircle, Clock, Sparkles } from 'lucide-react';

type ClassRowProps = {
  classItem: ClassInterface;
  onFavoriteChange?: (classId: string, isFavorite: boolean) => void;
  liveStreamClassFilter?: boolean;
};

// Maps a studio's required access tier to the cheapest plan name that unlocks it, mirrors the mobile app
const REQUIRED_PLAN_BY_TIER: { [tier: string]: string } = {
  LEVEL_2: 'Basic',
  LEVEL_3: 'Plus',
  LEVEL_4: 'Pro',
  LEVEL_5: 'Ultra',
  LEVEL_6: 'Holos Total',
};

const ClassRow: React.FC<ClassRowProps> = ({ classItem, onFavoriteChange, liveStreamClassFilter }) => {
  const { user } = useCurrentUser();
  const {
    studio,
    title,
    passes,
    promotionalTier,
    timeStamp = 0,
    timeStampEnd = 0,
    isFavorite,
    slug,
    id,
    vacancies = 0,
    isPartnerClosed = false,
    enrollTimeRestriction = 0,
    partnerClosedReason
  } = classItem;

  const districtName = typeof studio?.district === 'object' && studio?.district
    ? studio.district.name
    : "";
  const cityName = (typeof studio?.city === 'object' && (studio?.city as any)?.name)
    ? (studio.city as any).name
    : (studio?.address?.city || "");
  const hasPromotion = !!promotionalTier;
  // A promotional tier overrides the studio's normal tier
  const tier = promotionalTier || studio?.tier || "LEVEL_1";
  const isOutsideOfUsersAccessTier = user?.plan?.accessTier ? tier.replace('LEVEL_', '') > user.plan.accessTier.replace('LEVEL_', '') : false;
  const countryName = studio?.country?.name || "";
  const [isFavoriteLoading, setIsFavoriteLoading] = useState(false);
  const [localIsFavorite, setLocalIsFavorite] = useState(isFavorite || false);
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  const [isReservationConfirmedOpen, setIsReservationConfirmedOpen] = useState(false);
  const [reservationData, setReservationData] = useState<any>(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Cancel modal states
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isCancelErrorModalOpen, setIsCancelErrorModalOpen] = useState(false);
  const [cancelErrorMessage, setCancelErrorMessage] = useState<string>('');

  // Invite modal states
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isInviteSuccessModalOpen, setIsInviteSuccessModalOpen] = useState(false);

  // Update local state when prop changes
  useEffect(() => {
    setLocalIsFavorite(isFavorite || false);
  }, [isFavorite]);

  const startTime = format(new Date(timeStamp), "HH:mm", { locale: es });
  const durationMinutes = (timeStampEnd - timeStamp) / 60000;

  // Determine modality strictly from liveStreamClass (or active filter)
  const isLiveStream = typeof liveStreamClassFilter === 'boolean'
    ? liveStreamClassFilter
    : !!classItem.liveStreamClass;

  // Fallback flag if studio does not provide one
  const countryImage = studio?.country?.image;

  // Determine the inline status pill (closed / full / enroll time restriction), rendered inside the card like the mobile app
  const getStatusOverlay = () => {
    const now = new Date();
    const classStartTime = new Date(timeStamp);
    const hoursUntilClass = (classStartTime.getTime() - now.getTime()) / (1000 * 60 * 60);

    const isWithinEnrollRestriction = enrollTimeRestriction > 0 && hoursUntilClass < enrollTimeRestriction;

    // Priority order: 1. CLOSED, 2. NO VACANCIES, 3. TIME RESTRICTION
    if (isPartnerClosed) {
      return { text: "No disponible", icon: XCircle, type: 'closed' as const };
    }

    if (vacancies === 0) {
      return { text: "Agotada - no quedan vacantes", icon: MinusCircle, type: 'no_vacancies' as const };
    }

    if (isWithinEnrollRestriction) {
      return { text: "Reserva anticipada", icon: Clock, type: 'restriction' as const };
    }

    return null;
  };

  const statusOverlay = getStatusOverlay();
  const requiredPlanName = REQUIRED_PLAN_BY_TIER[tier] || '';
  const disabled = !!statusOverlay;
  const reservationDisabled = disabled || isOutsideOfUsersAccessTier;

  const [showClosureInfo, setShowClosureInfo] = useState(false);
  const [showPromoInfo, setShowPromoInfo] = useState(false);
  const normalPlanName = REQUIRED_PLAN_BY_TIER[studio?.tier || ''] || '';

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation to class detail
    e.stopPropagation(); // Prevent event bubbling

    if (isFavoriteLoading || !id) return; // Prevent multiple clicks or missing ID

    setIsFavoriteLoading(true);

    try {
      const success = localIsFavorite
        ? await removeFromFavorites(id)
        : await addToFavorites(id);

      if (success) {
        const newFavoriteState = !localIsFavorite;
        setLocalIsFavorite(newFavoriteState);

        // Notify parent component if callback provided
        if (onFavoriteChange) {
          onFavoriteChange(id, newFavoriteState);
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsFavoriteLoading(false);
    }
  };

  // Transform class data for the enrollment modal
  const getClassDataForModal = () => {
    const startDate = new Date(timeStamp);
    const endDate = new Date(timeStampEnd);

    return {
      title: title,
      studioName: studio.name || "Studio",
      passes,
      formattedClassDate: format(startDate, "EEEE dd", { locale: es }),
      formattedClassDateMonth: format(startDate, "MMMM", { locale: es }),
      formattedClassDateStartTime: format(startDate, "HH:mm", { locale: es }),
      formattedClassDateEndTime: format(endDate, "HH:mm", { locale: es }),
      street: studio.address?.street || "",
      districtName: districtName || "",
      state: studio.address?.state || studio.address?.city || "",
      cancelTimeRestriction: classItem.cancelTimeRestriction || 4,
      startTime: new Date(timeStamp).toISOString(),
      isVideo: false,
      corporate: false,
      liveStreamClass: classItem.liveStreamClass || false,
    };
  };

  // Handle enrollment
  const handleEnroll = async () => {
    try {
      if (!id) {
        setErrorMessage('ID de clase no disponible');
        setIsErrorModalOpen(true);
        return;
      }

      const enrollmentData: EnrollClassRequest = {
        classId: id,
        startday: timeStamp,
        endday: timeStampEnd,
        isClassProfile: true
      };

      console.log('Enrolling with data:', enrollmentData);

      const response = await enrollInClass(enrollmentData);

      if (response.success) {
        setIsEnrollModalOpen(false);

        // Prepare reservation data for the confirmation modal
        const reservationData = {
          classData: {
            title: title,
            timeStamp: new Date(timeStamp).toISOString(),
            timeStampEnd: new Date(timeStampEnd).toISOString(),
            studioName: studio.name || "",
            street: studio.address?.street || "",
            districtName: districtName || "",
            state: studio.address?.state || studio.address?.city || "",
            liveStreamClass: classItem.liveStreamClass || false,
            isVideo: false,
          },
          userClass: {
            userClassHash: {
              classHash: response.data?.userClassHash || "",
            },
          },
          userClassId: response.data?.userClassId || id,
          hash: response.data?.userClassHash || "",
        };

        setReservationData(reservationData);
        setIsReservationConfirmedOpen(true);
        console.log('Successfully enrolled in class');
      } else {
        setIsEnrollModalOpen(false);
        // Extract error message from response or use default
        const errorMsg = response.error ?
          (typeof response.error === 'string' ? response.error : 'Ha ocurrido un error inesperado') :
          'No se pudo procesar la reserva';
        setErrorMessage(errorMsg);
        setIsErrorModalOpen(true);
      }
    } catch (error) {
      setIsEnrollModalOpen(false);
      console.error('Error enrolling in class:', error);
      const errorMsg = error instanceof Error ? error.message : 'Ha ocurrido un error inesperado';
      setErrorMessage(errorMsg);
      setIsErrorModalOpen(true);
    }
  };

  // Handle reservar button click
  const handleReservarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (reservationDisabled) return;

    setIsEnrollModalOpen(true);
  };

  // Handle cancel class - opens the cancel modal
  const handleCancelClass = async (closeCallback: () => void) => {
    // Close the reservation confirmed modal
    closeCallback();
    // Open the cancel modal
    setIsCancelModalOpen(true);
  };

  // Handle actual cancellation
  const handleCancelClassConfirm = async (classId: string, _passes: number) => {
    try {
      const { status, message } = await cancelUserClass(classId);
      setIsCancelModalOpen(false);

      if (status !== 200) {
        throw new Error(message || 'Error al cancelar la clase');
      }

      setIsSuccessModalOpen(true);
    } catch (error) {
      console.error('Error cancelling class:', error);
      setCancelErrorMessage(error instanceof Error ? error.message : 'Error al cancelar la clase');
      setIsCancelErrorModalOpen(true);
    }
  };

  // Handle success modal close
  const handleSuccessModalClose = () => {
    setIsSuccessModalOpen(false);
    // Refresh the page to update the class list
    window.location.href = '/user/mis-clases';
  };

  // Handle invite friend
  const handleInviteFriend = async (email: string, userClassId: string) => {
    try {
      const response = await inviteFriendToClass(email, userClassId);

      if (response.success) {
        setIsInviteModalOpen(false);
        setIsInviteSuccessModalOpen(true);
      } else {
        setErrorMessage(response.error || 'Error al enviar la invitación');
        setIsErrorModalOpen(true);
      }
    } catch (error) {
      console.error('Error inviting friend:', error);
      setErrorMessage('Error al enviar la invitación');
      setIsErrorModalOpen(true);
    }
  };

  // Handle invite success modal close
  const handleInviteSuccessModalClose = () => {
    setIsInviteSuccessModalOpen(false);
  };

  return (
    <div className="relative mb-3">
      <Link
        to={disabled ? "#" : `/clases/${studio.slug}/${slug}/${timeStamp}`}
        className={`flex items-stretch gap-3 md:gap-4 w-full rounded-2xl border p-3 md:p-4 relative ${isOutsideOfUsersAccessTier
          ? `bg-[#f6f7ea] border-[#e5e7cf] ${disabled ? "opacity-60 cursor-default" : "hover:shadow-md transition-shadow cursor-pointer"}`
          : `bg-white border-gray-200 ${disabled ? "opacity-60 cursor-default" : "hover:shadow-md transition-shadow cursor-pointer"}`
          }`}
        onClick={disabled ? (e) => e.preventDefault() : undefined}
      >
        {/* Time & duration */}
        <div className="flex-none flex flex-col items-center justify-center text-center w-16 md:w-20">
          <span className="font-extrabold text-gray-900 text-sm md:text-base leading-tight">{startTime}</span>
          <span className="text-gray-400 text-xs mt-1">({durationMinutes} min)</span>
        </div>

        {/* Divider */}
        <div className="w-px bg-gray-200 flex-none" />

        {/* Content */}
        <div className="flex-grow min-w-0 py-0.5 md:flex md:items-center md:gap-6">
          {/* Title / studio */}
          <div className="min-w-0 md:w-52 md:flex-none">
            <div className="flex items-start justify-between gap-2 md:block">
              <h3 className="font-bold text-gray-900 text-sm md:text-base leading-snug break-words">{title}</h3>
              <button
                className={`flex-none md:hidden ${isFavoriteLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                style={{ background: "none", border: "none" }}
                onClick={handleFavoriteClick}
                disabled={isFavoriteLoading}
                aria-label="Favorito"
              >
                <Heart
                  className={`w-5 h-5 ${localIsFavorite ? "text-red-500" : "text-gray-300"}`}
                  fill={localIsFavorite ? "currentColor" : "none"}
                />
              </button>
            </div>
            <p className="text-holos uppercase font-light text-xs mt-0.5 truncate">{studio.name}</p>
          </div>

          {/* Location */}
          <div className="flex items-center text-gray-500 text-xs md:text-sm mt-0.5 md:mt-0 md:w-36 md:flex-none">
            {countryImage && (
              <div className="mr-1.5 h-[18px] w-[18px] flex-none bg-white rounded-full flex justify-center items-center border border-gray-300">
                <img src={countryImage} alt={countryName || "País"} className="h-[8px]" />
              </div>
            )}
            <span className="truncate">
              {districtName}{districtName && cityName ? ', ' : ''}{cityName}
            </span>
          </div>

          {/* Rating */}
          <div className="mt-1 md:mt-0 text-xs md:text-sm font-semibold text-gray-700 md:w-22">
            {Number(studio?.ratingCount) > 0 && (
              <span className="flex items-center md:flex-none">
                <Star className="w-3.5 h-3.5 text-yellow-400 mr-1" fill="currentColor" />
                <span className="whitespace-nowrap">
                  {((studio?.rating || 0) + 1).toFixed(1)} ({String(studio?.ratingCount)})
                </span>
              </span >
            )}
          </div>

          {/* Badges: access tier / enroll restriction / closed-full-restricted status */}
          <div className="flex flex-wrap items-center gap-2 mt-2 md:mt-0 md:w-48 md:flex-none">
            {hasPromotion && (
              <div className="relative inline-flex items-center gap-1 flex-none">
                <div
                  className="flex-none inline-flex items-center rounded-full justify-center text-center bg-prosper/70 px-3 py-1 text-[10px] font-bold uppercase text-black">
                  <Sparkles className="w-3.5 h-3.5 mr-1" />
                  Oferta
                  <button
                    type="button"
                    aria-label="Información de la oferta"
                    className="cursor-pointer text-gray-500 ml-2"
                    style={{ background: "none", border: "none" }}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowPromoInfo((s) => !s); }}
                  >
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </div>
                {showPromoInfo && (
                  <div className="absolute top-full left-0 mt-1 bg-gray-800 text-white text-[11px] rounded px-2 py-1 shadow z-10 w-[220px] whitespace-normal">
                    {normalPlanName
                      ? `Esta clase normalmente solo está disponible en el plan ${normalPlanName}, pero ahora tiene una oferta.`
                      : 'Esta clase tiene una oferta por tiempo limitado.'}
                  </div>
                )}
              </div>
            )}

            {isOutsideOfUsersAccessTier && requiredPlanName && (
              <div className="inline-flex items-center gap-1.5 bg-[#F2F5C8] rounded-full px-3 py-1 text-[10px] font-bold uppercase text-black">
                <Lock className="w-3 h-3" />
                Disponible desde {requiredPlanName}
              </div>
            )}

            {enrollTimeRestriction > 0.5 && (
              <span className="inline-block bg-red text-white rounded-full px-2 py-1 text-[10px] font-publicsans">
                {enrollTimeRestriction <= 72
                  ? enrollTimeRestriction + ' HRS'
                  : enrollTimeRestriction / 24 + ' DÍAS'}
              </span>
            )}

            {statusOverlay && (
              <div className="relative inline-block">
                {statusOverlay.type === 'closed' && partnerClosedReason ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 bg-gray-100 rounded-full px-3 py-1 text-[10px] font-bold text-gray-700 cursor-pointer"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowClosureInfo((s) => !s); }}
                  >
                    <statusOverlay.icon className="w-3.5 h-3.5" />
                    {statusOverlay.text}
                    <Info className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <div className="inline-flex items-center gap-1.5 bg-gray-100 rounded-full px-3 py-1 text-[10px] font-bold text-gray-700">
                    <statusOverlay.icon className="w-3.5 h-3.5" />
                    {statusOverlay.text}
                  </div>
                )}
                {showClosureInfo && (
                  <div className="absolute top-full left-0 mt-1 bg-gray-800 text-white text-[11px] rounded px-2 py-1 shadow z-10 max-w-[220px] whitespace-normal">
                    {partnerClosedReason}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Type label + Reservar + favorite (favorite is inline with title on mobile instead) */}
          <div className="flex items-center justify-between mt-3 md:mt-0 md:flex-1 md:justify-end md:gap-4">
            <ClassTypeLabel liveStreamClass={isLiveStream} isVideo={false} />
            <button
              className={`uppercase px-4 py-1.5 rounded-lg text-xs font-semibold ${reservationDisabled
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-holos-dark text-white cursor-pointer"
                }`}
              disabled={reservationDisabled}
              onClick={handleReservarClick}
            >
              Reservar
            </button>
            <button
              className={`hidden md:inline-flex flex-none ${isFavoriteLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              style={{ background: "none", border: "none" }}
              onClick={handleFavoriteClick}
              disabled={isFavoriteLoading}
              aria-label="Favorito"
            >
              <Heart
                className={`w-5 h-5 ${localIsFavorite ? "text-red-500" : "text-gray-300"}`}
                fill={localIsFavorite ? "currentColor" : "none"}
              />
            </button>
          </div>
        </div>
      </Link>

      {/* Enrollment Modal */}
      <EnrollInClassModal
        isOpen={isEnrollModalOpen}
        onClose={() => setIsEnrollModalOpen(false)}
        classInfo={getClassDataForModal()}
        onEnrollConfirmed={handleEnroll}
      />

      {/* Reservation Confirmed Modal */}
      {reservationData && (
        <ReservationConfirmedModal
          isOpen={isReservationConfirmedOpen}
          onClose={() => setIsReservationConfirmedOpen(false)}
          classData={reservationData.classData}
          userClass={reservationData.userClass}
          userClassId={reservationData.userClassId}
          hash={reservationData.hash}
          cancelClass={handleCancelClass}
          onInvite={() => setIsInviteModalOpen(true)}
        />
      )}

      {/* Cancel Class Modal */}
      {isCancelModalOpen && reservationData && (
        <CancelClassModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          cancelClassData={{
            classId: reservationData.userClassId,
            passes,
            classStartTime: reservationData.classData.timeStamp,
            classEndTime: reservationData.classData.timeStampEnd,
            cancelTimeRestriction: classItem.cancelTimeRestriction || 0,
            isVideo: false,
            studioName: reservationData.classData.studioName,
            className: reservationData.classData.title
          }}
          cancelClassConfirm={handleCancelClassConfirm}
        />
      )}

      {/* Success Modal */}
      <SuccessModal
        isOpen={isSuccessModalOpen}
        onClose={handleSuccessModalClose}
        title="¡Clase Cancelada!"
        message="Tu clase ha sido cancelada satisfactoriamente. Puedes revisar tu agenda para confirmar los cambios."
        buttonText="Ver Mi Agenda"
      />

      {/* Invite Friend Modal */}
      {isInviteModalOpen && reservationData && (
        <InviteAFriendModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          userClass={{
            _id: reservationData.userClassId,
            startTime: reservationData.classData.timeStamp,
            endTime: reservationData.classData.timeStampEnd,
            class: {
              title: reservationData.classData.title,
              studio: {
                name: reservationData.classData.studioName,
                address: {
                  street: reservationData.classData.street,
                  state: reservationData.classData.state
                },
                district: reservationData.classData.districtName ? { name: reservationData.classData.districtName } : undefined
              }
            }
          }}
          user={{
            remainingClassInvites: 5, // This should come from user context
            plan: {
              numberOfPasses: passes
            }
          }}
          liveStreamClass={classItem.liveStreamClass || false}
          isVideo={false}
          onInvite={handleInviteFriend}
        />
      )}

      {/* Invite Success Modal */}
      <SuccessModal
        isOpen={isInviteSuccessModalOpen}
        onClose={handleInviteSuccessModalClose}
        title="¡Invitación Enviada!"
        message="Tu invitación ha sido enviada exitosamente. Tu amigo recibirá la invitación en su correo y su clase no será agendada hasta que acepte en su cuenta."
        buttonText="Aceptar"
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={isErrorModalOpen}
        onClose={() => {
          setIsErrorModalOpen(false);
          setErrorMessage('');
        }}
        title="Error al Reservar"
        message={errorMessage}
      />

      <ErrorModal
        isOpen={isCancelErrorModalOpen}
        onClose={() => {
          setIsCancelErrorModalOpen(false);
          setCancelErrorMessage('');
        }}
        title="Error al Cancelar Clase"
        message={cancelErrorMessage}
        onRetry={() => {
          setIsCancelErrorModalOpen(false);
          setIsCancelModalOpen(true);
        }}
        retryText="Intentar de Nuevo"
      />
    </div>
  );
};

export default ClassRow;
